import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { useProduct } from "@/components/pdp/PdpDataContext";

export const ActivesSection = () => {
  const { actives } = useProduct();
  // Multi-open accordion: any number of rows can stay expanded at once; a
  // row only closes when the shopper explicitly taps it again. Starts with
  // the first ingredient open so the section doesn't render blank.
  const [openSet, setOpenSet] = useState(() => new Set([0]));
  const toggle = (i) => setOpenSet((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  const allOpen = openSet.size === actives.items.length;
  const toggleAll = () =>
    setOpenSet(allOpen ? new Set() : new Set(actives.items.map((_, i) => i)));
  const [showFull, setShowFull] = useState(false);

  return (
    <section className="bg-white py-20 lg:py-32" data-testid="actives-section">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
                {actives.eyebrow}
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900 mb-6">
                {actives.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
              </h2>
              <p className="text-[15px] leading-relaxed text-neutral-600 max-w-sm">
                {actives.intro}
              </p>
              <AnimatePresence initial={false}>
                {showFull && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="mt-6 text-[13px] leading-relaxed text-neutral-700 max-w-sm">
                      Eye Repair Treatment Water/Eau, Caprylic/Capric Triglycerides, Propylene Glycol, Shea Butter, Polysorbate 60, Cyclopentasiloxane, Cyclotetrasiloxane, Dimethiconol, Glycerin, Butylene Glycol, Propylene Glycol Alginate, Hinokitiol, Palmitoyl Pentapeptide-4, Hesperidin Methyl Chalcone, N-Hydroxysuccinimide, Chrysin, Tocopheryl Acetate, Sodium Hydroxide, Caprylyl Glycol, Phenoxyethanol, Sodium Tocopheryl Phosphate, Sodium Lauriminodipropionate, Polysorbate 20, Xanthan Gum, Sodium Citrate, Alcohol, Palmitoyl Oligopeptide, Aminopropyl Ascorbyl Phosphate, Steareth-20, Carbomer, Tetrasodium EDTA, Citric Acid, Sorbic Acid.
                    </p>
                    <p className="mt-4 text-[13px] leading-relaxed text-neutral-700 max-w-sm">
                      Our formulations are updated from time to time as part of our commitment to innovation. As such, the ingredient list shown here may vary from the box depending on the time of purchase.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
              <button
                onClick={() => setShowFull((v) => !v)}
                data-testid="actives-expand-all"
                className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-riversol-blue border-b border-riversol-blue/40 pb-1 hover:border-riversol-blue transition-colors"
              >
                {showFull ? "Hide full ingredients list" : "See full ingredients list"}
                <Plus
                  size={14}
                  className={`transition-transform duration-300 ${showFull ? "rotate-45" : ""}`}
                />
              </button>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="border-t border-neutral-200">
              {actives.items.map((a, i) => {
                const isOpen = openSet.has(i);
                return (
                  <div key={a.n} className="border-b border-neutral-200">
                    <button
                      onClick={() => toggle(i)}
                      data-testid={`active-toggle-${i}`}
                      className="w-full flex items-start gap-8 py-7 text-left group"
                    >
                      <span className="font-mono text-sm text-riversol-blue pt-1 w-8 shrink-0">{a.n}</span>
                      <div className="flex-1">
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-400 mb-1">{a.cat}</div>
                        <div className="text-xl text-neutral-900 group-hover:text-riversol-blue transition-colors">{a.name}</div>
                      </div>
                      <Plus
                        size={20}
                        className={`text-neutral-400 mt-1 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-45 text-riversol-blue" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="text-[15px] leading-relaxed text-neutral-600 pb-7 pl-4 lg:pl-16 max-w-2xl">
                            {a.benefit}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivesSection;
